/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { useEffect, useState } from 'react';

import classNames from 'classnames';

import useRouter from 'js/lib/useRouter';

import { SelectOption, SilentSelectField, VisuallyHidden } from '@coursera/cds-core';

import NewThreadButton from 'bundles/discussions/components/NewThreadButton';
import SearchBox from 'bundles/discussions/components/search/SearchBox';
import type { LoadingState, QuestionSort } from 'bundles/discussions/constants';
import { loadingStates, questionSorts } from 'bundles/discussions/constants';

import _t from 'i18n!nls/discussions';

import 'css!./__styles__/ControlsBar';

const { mostRecentSort, popularSort, unansweredSort } = questionSorts;

export type Props = {
  inSearch: boolean;
  toggleSearch: () => void;
  sort?: QuestionSort;
  showSearchBox?: boolean;
  loadingState: LoadingState;
};

const styles = {
  leftSection: () => css`
    padding-left: var(--cds-spacing-300);
  `,
  rightSection: () => css`
    display: flex;
    justify-content: flex-end;
    align-items: center;
    padding: var(--cds-spacing-150) 0;

    // This is a workaround to override the margin of the new thread button without impacting other pages
    .rc-NewThreadForm .c-new-thread-button {
      margin: 0 !important;
    }
  `,
};

const ControlsBar = ({ sort = mostRecentSort, showSearchBox = true, inSearch, toggleSearch, loadingState }: Props) => {
  const router = useRouter();
  const [sortNotification, setSortNotification] = useState('');

  useEffect(() => {
    const sortsText = {
      [mostRecentSort]: _t('latest'),
      [popularSort]: _t('top'),
      [unansweredSort]: _t('unanswered'),
    };

    if (loadingState === loadingStates.DONE) {
      setSortNotification(
        _t('Discussions are sorted by #{sort}', {
          sort: sortsText[sort],
        })
      );
    }

    if (loadingState === loadingStates.LOADING) {
      setSortNotification('');
    }
  }, [loadingState, sort]);

  const handleChangeSort = (e: $TSFixMe) => {
    const newPathName = window.location.pathname + '?sort=' + e.target.value + '&page=1';
    router.push(newPathName);
  };

  const classes = classNames('rc-ControlsBar', 'styleguide', 'horizontal-box', 'align-items-vertical-center', {
    'in-search-mode': inSearch,
  });

  return (
    <div className={classes}>
      <div className="shrink-block" css={styles.leftSection}>
        <SilentSelectField defaultValue={sort} id="sortOrder" onChange={handleChangeSort} label={_t('Sort by')}>
          <SelectOption key={mostRecentSort} value={mostRecentSort}>
            {_t('Latest')}
          </SelectOption>
          <SelectOption key={popularSort} value={popularSort}>
            {_t('Top')}
          </SelectOption>
          <SelectOption key={unansweredSort} value={unansweredSort}>
            {_t('Unanswered')}
          </SelectOption>
        </SilentSelectField>

        <VisuallyHidden role="status" aria-live="polite" component="span" className="rc-A11yScreenReaderOnly">
          {sortNotification}
        </VisuallyHidden>
      </div>

      <div className="flex-1" css={styles.rightSection}>
        {showSearchBox && (
          <SearchBox
            previousElementId="sortOrder"
            nextElementId="new-thread-btn"
            animateToSearch={toggleSearch}
            animateFromSearch={toggleSearch}
          />
        )}
        <NewThreadButton />
      </div>
    </div>
  );
};

export default ControlsBar;
